import { Box, CircularProgress, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
//import { WistiaPlayer } from "@wistia/wistia-player-react";

interface WistiaVideoProps {
  mediaId: string;
  aspectRatio?: number;
}

const WistiaVideo: React.FC<WistiaVideoProps> = ({
  mediaId,
  aspectRatio = 16 / 9,
}) => {
  const theme = useTheme();
  const [isLoaded, setIsLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const playerColor = theme.palette.primary.main;

  useEffect(() => {
    let isMounted = true;

    const loadScript = (src: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        const existingScript = document.querySelector(`script[src="${src}"]`);
        if (existingScript) {
          resolve();
          return;
        }

        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Failed to load ${src}`));
        document.body.appendChild(script);
      });
    };

    const loadWistia = async () => {
      try {
        await Promise.all([
          loadScript(`https://fast.wistia.com/embed/medias/${mediaId}.jsonp`),
          loadScript("https://fast.wistia.com/assets/external/E-v1.js"),
        ]);

        if (!isMounted) return;

        (window as any)._wq = (window as any)._wq || [];
        (window as any)._wq.push({
          id: mediaId,
          options: {
            mediaId,
            playerColor: playerColor, // cambia el color primario (botones, barra, etc.)
            volumeControl: false, // oculta el control de volumen
            fullscreenControl: false,
            fullscreenButton: false, // elimina el botón fullscreen
            settingsControl: false, // elimina el engranaje de ajustes
          },
          onReady: function (video: any) {
            if (isMounted) {
              setIsLoaded(true);
            }
          },
        });

        setTimeout(() => {
          if (isMounted && !isLoaded) setIsLoaded(true);
        }, 5000);
      } catch (err) {
        console.error("Error loading Wistia scripts", err);
        setIsLoaded(true);
      }
    };

    loadWistia();

    return () => {
      isMounted = false;
      (window as any)._wq = (window as any)._wq?.filter(
        (item: any) => item.id !== mediaId
      );
    };
  }, [mediaId, isLoaded, playerColor]);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        borderRadius: 2,
        overflow: "hidden",
        boxShadow: 3,
        backgroundColor: theme.palette.grey[900],
        ...(aspectRatio && {
          "&::before": {
            content: '""',
            display: "block",
            paddingTop: `${(1 / aspectRatio) * 100}%`,
          },
        }),
      }}
    >
      {!isLoaded && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(0,0,0,0.4)",
            zIndex: 1,
          }}
        >
          <CircularProgress color="warning" />
        </Box>
      )}
      <Box
        ref={containerRef}
        sx={{
          position: aspectRatio ? "absolute" : "relative",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}
      >
        <div
          className={`wistia_embed wistia_async_${mediaId}`}
          style={{ height: "100%", width: "100%" }}
        />
      </Box>
    </Box>
  );
};

export default WistiaVideo;
